import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import ProgressBar from './ProgressBar';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const FundraiserDetailPage = ({ fundraisers, onDonationComplete }) => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [donationAmount, setDonationAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fundraiser = fundraisers.find(f => f.id === parseInt(id));

  useEffect(() => {
    const success = searchParams.get('success');
    const amount = searchParams.get('amount');
    const canceled = searchParams.get('canceled');
    
    if (success === 'true' && amount && fundraiser) {
      const donationAmount = parseFloat(amount);
      onDonationComplete(fundraiser.id, donationAmount);
      setSuccessMessage(`Thank you! Your donation of $${donationAmount} has been processed successfully. Amounts will update within 30 seconds.`);
      
      setSearchParams({});
    } else if (canceled === 'true') {
      setError('Payment was canceled. You can try again if you wish.');
      setSearchParams({});
    }
  }, [searchParams, fundraiser, onDonationComplete, setSearchParams]);

  if (!fundraiser) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Fundraiser not found</h2>
        <Link to="/" className="text-blue-600 hover:text-blue-800">
          ← Back to all fundraisers
        </Link>
      </div>
    );
  }

  const handleDonate = async (e) => {
    e.preventDefault();
    
    if (!donationAmount || isNaN(donationAmount) || parseFloat(donationAmount) <= 0) {
      setError('Please enter a valid donation amount');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const stripe = await stripePromise;
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';
      const response = await fetch(`${apiUrl}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: parseFloat(donationAmount),
          fundraiserName: fundraiser.name,
          fundraiserId: fundraiser.id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        // Check if it's a demo mode error
        if (errorData.demoMode) {
          setError('💳 ' + errorData.error);
          setIsLoading(false);
          return;
        }

        throw new Error('Failed to create checkout session');
      }

      const session = await response.json();

      const result = await stripe.redirectToCheckout({
        sessionId: session.id,
      });

      if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError('Payments are disabled in demo mode. This is a showcase environment only.');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const suggestedAmounts = [25, 50, 100, 250];

  return (
    <div className="container mx-auto px-4 py-8">
      <Link to="/" className="text-blue-600 hover:text-blue-800 mb-6 inline-block">
        ← Back to all fundraisers
      </Link>

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/3">
              <img 
                src={fundraiser.photo} 
                alt={fundraiser.name}
                className="w-full h-64 md:h-full object-cover"
              />
            </div>
            
            <div className="md:w-2/3 p-8">
              <h1 className="text-3xl font-bold text-gray-800 mb-4">{fundraiser.name}</h1>
              <div className="prose text-gray-600 mb-6">
                <p>{fundraiser.fullBio}</p>
              </div>
              
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Fundraising Progress</h3>
                <ProgressBar 
                  goal={fundraiser.goal} 
                  raised={fundraiser.raised}
                  showLegend={true}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Support {fundraiser.name.split(' ')[0]}'s Mission</h2>
          
          <form onSubmit={handleDonate} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Donation Amount
              </label>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {suggestedAmounts.map(amount => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setDonationAmount(amount.toString())}
                    className={`py-2 px-4 border rounded-lg text-center transition-colors ${
                      donationAmount === amount.toString()
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
              
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="text-green-600 text-sm bg-green-50 p-3 rounded-lg">
                {successMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !donationAmount}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Processing...' : `Donate $${donationAmount || '0'}`}
            </button>
          </form>

          <div className="mt-6 text-sm text-gray-500">
            <p>💳 Secure payment processed by Stripe</p>
            <p>🔒 Your payment information is encrypted and secure</p>
            <p>📧 You'll receive an email receipt after your donation</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundraiserDetailPage;